"use client";

import { useEffect, useRef } from "react";

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);
  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      if (parallaxRef.current) {
        parallaxRef.current.style.transform = `translate(${x * 15}px, ${y * 10}px)`;
      }
    };

    const el = heroRef.current;
    el?.addEventListener("mousemove", handleMouseMove);
    return () => el?.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const athleticLines = [
  { width: "200px", top: "20%", left: "5%", delay: "0s", rot: "-15deg" },
  { width: "120px", top: "40%", left: "88%", delay: "1.5s", rot: "10deg" },
  { width: "160px", top: "70%", left: "15%", delay: "3s", rot: "5deg" },
  { width: "90px", top: "15%", left: "75%", delay: "2s", rot: "-8deg" },
  { width: "140px", top: "80%", left: "70%", delay: "0.8s", rot: "20deg" }];


  return (
    <section
      ref={heroRef}
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden grain-overlay"
      style={{ background: "var(--navy-dark)" }}>

      {/* Gradient Mesh Background */}
      <div
        className="mesh-bg absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at 15% 50%, rgba(36, 66, 122, 0.8) 0%, transparent 55%), radial-gradient(ellipse at 85% 15%, rgba(224, 96, 49, 0.25) 0%, transparent 45%), radial-gradient(ellipse at 55% 85%, rgba(27, 54, 93, 0.9) 0%, transparent 50%)"
        }} />


      {/* Orbit Rings */}
      <div
        className="orbit-ring"
        style={{ width: "600px", height: "600px", top: "50%", left: "50%", marginTop: "-300px", marginLeft: "-300px" }} />

      <div
        className="orbit-ring"
        style={{ width: "900px", height: "900px", top: "50%", left: "50%", marginTop: "-450px", marginLeft: "-450px", animationDirection: "reverse", animationDuration: "45s", borderColor: "rgba(224,96,49,0.06)" }} />


      {/* Athletic Floating Lines */}
      {athleticLines.map((line, i) =>
      <div
        key={i}
        className="athletic-line"
        style={{
          width: line.width,
          top: line.top,
          left: line.left,
          animationDelay: line.delay,
          "--rot": line.rot
        } as React.CSSProperties} />

      )}

      {/* Action Silhouette Overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1544717297-fa95b6ee9643?auto=format&fit=crop&w=2000&q=10')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.04,
          filter: "grayscale(100%)"
        }} />


      {/* Light Sweep */}
      <div className="light-sweep" />

      {/* Hero Content */}
      <div ref={parallaxRef} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 text-center transition-transform duration-100 pt-20 mb-5 pb-0">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 mb-6 sm:mb-8 px-4 sm:px-5 py-2 rounded-full glass-card mt-8 pt-2">
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ background: "var(--terracotta)", boxShadow: "0 0 8px var(--terracotta)" }} />

          <span className="section-label" style={{ fontSize: "0.6rem" }}>
            Now Open · Doddajala, Bengaluru
          </span>
        </div>

        {/* Main Headline */}
        <h1 className="headline-xl text-white mb-4 sm:mb-6 max-w-5xl mx-auto">
          Fuel Your{" "}
          <span className="gradient-text">Game.</span>
          <br />
          Elevate Your{" "}
          <span
            className="font-display italic"
            style={{ color: "rgba(255,255,255,0.85)" }}>

            Performance.
          </span>
        </h1>

        {/* Subtext */}
        <p
          className="text-white/60 text-base sm:text-lg md:text-xl font-medium mb-8 sm:mb-12 max-w-2xl mx-auto leading-relaxed px-2">

          Elite Tennis, Pickleball, Swimming & Fitness —
          <br className="hidden md:block" /> All Under One Roof.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-10 sm:mb-16 px-2">
          <a href="#membership" className="btn-primary pulse-cta text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4 w-full sm:w-auto justify-center">
            <span>Join the Revolution</span>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </a>
          <a href="#contact" className="btn-secondary text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4 w-full sm:w-auto justify-center">
            <span>Book a Session</span>
          </a>
        </div>

        {/* Stats Row */}
        <div className="flex items-center justify-center gap-6 sm:gap-8 md:gap-16 pb-5">
          {[
          { value: "4", unit: "Sports", label: "Disciplines" },
          { value: "500+", unit: "", label: "Active Members" },
          { value: "4.9", unit: "★", label: "Community Rating" }].
          map((stat) =>
          <div key={stat.label} className="text-center">
              <div className="text-2xl sm:text-3xl font-bold text-white">
                {stat.value}
                <span style={{ color: "var(--terracotta)" }}>{stat.unit}</span>
              </div>
              <div className="text-white/40 text-xs font-semibold uppercase tracking-widest mt-1">
                {stat.label}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10">
        <div
          className="w-px h-12 mt-0 pt-0"
          style={{
            background: "linear-gradient(to bottom, rgba(224,96,49,0.6), transparent)"
          }} />

      </div>
    </section>);

}